import { styled, Typography } from '@mui/material'
import Link from 'next/link'
import { ContactVariables } from '@/GlobalVariables/Contact'
import { colors } from '@/Theme/LegacyTheme'

const ListItem = styled('ul')(() => ({}))

export const customerCareFaqItems = [
  {
    title: "What can you expect after you've registered your complaint?",
    defaultExpanded: true,
    content: (
      <>
        <Typography variant="body1">
          Where we receive a complaint from you, our Customer Care Team will investigate your
          complaint competently, diligently and impartially.
        </Typography>
        <Typography variant="body1" sx={{ mb: 0 }}>
          We&apos;ll fairly, consistently and promptly assess:
        </Typography>
        <ListItem sx={{ mb: 4 }}>
          <li>The nature of your complaint.</li>
          <li>
            Whether any third party is involved in the complaint (such as the business that sold
            your vehicle or a lender).
          </li>
          <li>How we should resolve the complaint.</li>
          <li>Whether the complaint should be upheld and whether any redress should be paid to you.</li>
        </ListItem>
        <Typography variant="body1">
          When considering complaints, we&apos;ll take into account all of the available evidence –
          as well as the circumstances of your particular complaint. We&apos;ll also take into
          account any guidance published by the Financial Ombudsman Service (&lsquo;the
          Ombudsman&rsquo;), as well as any relevant laws or regulation.
        </Typography>
      </>
    )
  },
  {
    title: 'How will we resolve your complaint?',
    content: (
      <>
        <Typography variant="body1">
          Upon receiving your complaint we&apos;ll acknowledge receipt within three working days
          and send you a written or electronic communication to inform you of how we plan to
          investigate it, including a copy of our complaints handling procedure for your
          reference.
        </Typography>
        <Typography variant="body1">
          During the course of our investigation we may need to obtain detailed information from
          you or any third party involved in the complaint. In order to deal with your complaint
          promptly and thoroughly we ask that you help as much as possible with our assessment
          and your prompt attention would be gratefully appreciated.
        </Typography>
        <Typography variant="body1">
          We&apos;ll keep in touch while we&apos;re looking into your complaint and we&apos;ll be
          happy to answer any queries you may have about how we&apos;re progressing.
        </Typography>
        <Typography variant="body1">
          If the complaint isn&apos;t resolved after a period of 8 weeks we&apos;ll send you a
          written or electronic response with either our Final Response or to explain why
          we&apos;ve not been able to complete our investigation.
        </Typography>
        <Typography variant="body1">
          Please see the FAQ on{' '}
          <strong>
            What does the latest FCA review mean to how my complaint will be handled?
          </strong>{' '}
          for the detail on how this requirement may be different regarding complaints relating
          to discretionary commission arrangements.
        </Typography>
        <Typography variant="body1">
          If we haven&apos;t been able to finalise our investigation within 8 weeks, we&apos;ll
          also give you an indication of when we expect to be able to provide you with a Final
          Response. You&apos;ll also receive a copy of the Ombudsman&apos;s explanatory leaflet,
          setting out further details of the service they provide and how to refer your
          complaint to them.
        </Typography>
      </>
    )
  },
  {
    title: 'What happens after the investigation?',
    content: (
      <Typography variant="body1">
        Where we decide that redress is appropriate, we&apos;ll provide you with fair
        compensation for any acts or omissions which we&apos;re responsible for and comply
        promptly with any offer of redress which you may accept. The redress won&apos;t always
        involve financial compensation and may instead include, for example, an apology, repair
        of the financed vehicle or an offer to rewrite a finance application. In cases where
        financial redress is deemed appropriate, this may also include an element of interest
        calculated in line with relevant guidance.
      </Typography>
    )
  },
  {
    title: 'Time limits for making a complaint',
    content: (
      <>
        <Typography variant="body1">
          We have the right to decline an investigation of any complaint received more than 6
          years from the date of the incident you&apos;ve complained about, or (if later) more
          than 3 years after you were (or ought reasonably to have been) aware of a cause for
          complaint.
        </Typography>
        <Typography variant="body1">
          We will, however, be happy to consider any exceptional circumstances that you feel may
          explain the delay in bringing the matter to our attention.
        </Typography>
      </>
    )
  },
  {
    title: 'What can you do if you are unhappy with our response?',
    content: (
      <>
        <Typography variant="body1">
          Should you remain unhappy with our findings, having received our &lsquo;Final
          Response&rsquo;, you may be able to refer the matter to the Ombudsman for review and
          we&apos;ll advise you of your rights to this.
        </Typography>
        <Typography variant="body1" sx={{ mb: 0 }}>
          You can refer your complaint to the Ombudsman if you&apos;ve either:
        </Typography>
        <ListItem sx={{ mb: 4 }}>
          <li>
            Received a final response with which you&apos;re dissatisfied; or
            <Typography variant="body1" sx={{ mt: 2, mb: 0 }}>
              (Please see the FAQ on{' '}
              <strong>
                What does the latest FCA review mean to how my complaint will be handled?
              </strong>{' '}
              for the detail on how this requirement may be different regarding complaints
              relating to discretionary commission arrangements.)
            </Typography>
          </li>
          <li>
            We&apos;ve had at least 8 weeks to consider the complaint but haven&apos;t yet issued a
            final response.
            <Typography variant="body1" sx={{ mt: 2, mb: 0 }}>
              (Please see the FAQ on{' '}
              <strong>
                What does the latest FCA review mean to how my complaint will be handled?
              </strong>{' '}
              for the detail on how this requirement may be different regarding complaints
              relating to discretionary commission arrangements.)
            </Typography>
          </li>
        </ListItem>
        <Typography variant="body1">
          If you do want the Ombudsman to look into your complaint, you should contact them within
          6 months of the date of our final response letter – a copy of which they may ask you to
          send to them.
        </Typography>
        <Typography variant="body1">
          The Ombudsman does not govern all of the services that Zuto provide so it is possible
          that they may not be able to assist you. We will, however, inform you in our final
          response if we think this is likely to be the case and you&apos;ll still be able to
          contact the Ombudsman who&apos;ll make their own decision on whether your case is
          eligible for them to look in to.
        </Typography>
        <Typography variant="body1">
          The Ombudsman is a free and impartial service for resolving disputes between consumers
          and financial services institutions and their contact details are set out below.
        </Typography>
        <Typography color={colors.darkGreen} variant="body1" sx={{ mb: 0 }}>
          By post:
        </Typography>
        <address style={{ marginBottom: 24, fontStyle: 'normal' }}>
          The Financial Ombudsman Service
          <br />
          Exchange Tower
          <br />
          London
          <br />
          E14 9SR
        </address>
        <Typography color={colors.darkGreen} variant="body1" sx={{ mb: 0 }}>
          By telephone:
        </Typography>
        <Typography variant="body1">
          <Link href="tel:0800 023 4 567" role="link">
            0800 023 4 567
          </Link>{' '}
          or{' '}
          <Link href="tel:0300 123 9 123" role="link">
            0300 123 9 123
          </Link>
        </Typography>
        <Typography color={colors.darkGreen} variant="body1" sx={{ mb: 0 }}>
          By email:
        </Typography>
        <Typography variant="body1">
          <Link href="mailto:complaint.info@financialombudsman.org.uk" role="link">
            complaint.info@financialombudsman.org.uk
          </Link>
        </Typography>
        <Typography color={colors.darkGreen} variant="body1" sx={{ mb: 0 }}>
          Website:
        </Typography>
        <Typography variant="body1">
          <Link
            href="https://www.financial-ombudsman.org.uk"
            target="_blank"
            role="link"
          >
            www.financial-ombudsman.org.uk
          </Link>
        </Typography>
        <Typography variant="body1">
          We&apos;ll maintain records and provide the Ombudsman or the Financial Conduct
          Authority, on request, details of all complaints handled by us.
        </Typography>
        <Typography variant="body1">
          A copy of this document will be issued with every acknowledgement letter sent to the
          customer following receipt of a complaint.
        </Typography>
        <Typography variant="body1">
          A copy of this document is also published on the zuto.com website to inform any
          customers wishing to raise a complaint of our Customer Care Team contact details.
        </Typography>
        <Typography variant="body1" sx={{ mb: 0 }}>
          An email is also sent to every customer following the finance being paid out. Within
          this email is the following wording &ldquo;If there&apos;s something that you are not
          happy with, let us know so that we can put things right. Please contact our Customer
          Care Team by writing to us at the address below, by telephoning us on{' '}
          <Link href={`tel:${ContactVariables.zutoContactNumber}`} role="link">
            {ContactVariables.zutoContactNumber}
          </Link>{' '}
          or by email at{' '}
          <Link href="mailto:customercare@zuto.com" role="link">
            customercare@zuto.com
          </Link>
          .&rdquo;
        </Typography>
      </>
    )
  },
  {
    title: 'What does the latest FCA review mean to how my complaint will be handled?',
    content: (
      <>
        <Typography variant="body1">
          The FCA is currently undertaking a review of historic discretionary commission
          arrangements (DCA) within the motor finance sector and, to support this, they have made
          some changes to complaints rules relating to DCA. The key change that could impact our
          customers when making a complaint is the implementation of a &ldquo;pause&rdquo; to the
          normal 8-week response timescales that all firms, including Zuto, have to respond to
          complaints that relate to DCA. During this pause, we will continue to investigate any
          complaint relating to DCA, but you may not receive a final response to your complaint
          until the FCA has concluded their investigation. Under current rules we are not expected
          to issue a final response until after 31st May 2026.
        </Typography>
        <Typography variant="body1" sx={{ mb: 0 }}>
          Further details in regards to what this means for you, your complaint and your FOS
          rights can be found here:{' '}
          <Link
            href="https://www.fca.org.uk/consumers/car-finance-complaints"
            target="_blank"
            role="link"
          >
            FCA - Car Finance Claims
          </Link>
        </Typography>
      </>
    )
  }
]
